import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtime } from "@/contexts/RealtimeContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel,
         AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
         AlertDialogHeader, AlertDialogTitle,
         AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Car, Plus, Pencil, Trash2, Mailbox, Info, KeyRound } from "lucide-react";
import { GuestPassesCard } from "@/components/GuestPassesCard";

/**
 * Resident Parking & Mailboxes — three sections:
 *   1. My Mailbox        — read-only lookup of admin-assigned mailbox
 *   2. My Vehicles       — full CRUD, each edit re-triggers admin review
 *   3. My Parking Spots  — read-only view of admin-assigned spots for my unit
 *
 * Backend endpoints:
 *   GET/POST/PATCH/DELETE /api/me/vehicles
 *   GET /api/me/mailbox
 *   GET /api/parking?property_id=… (filtered client-side by unit)
 */
export default function ResidentParkingPage() {
  const { user } = useAuth();
  const { subscribe } = useRealtime();
  const [vehicles, setVehicles] = useState([]);
  const [mailbox, setMailbox] = useState(null);
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // vehicle being edited (or null for new)
  const [form, setForm] = useState({
    make: "", model: "", year: "", color: "", plate: "",
    permit_number: "", notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [v, m] = await Promise.all([
        api.get("/me/vehicles"),
        api.get("/me/mailbox"),
      ]);
      setVehicles(v.data?.items || []);
      setMailbox(m.data || null);
      // Parking spots (admin-assigned) — filter by unit client-side since /parking is property-scoped
      const propId = (user?.property_ids || [])[0];
      if (propId) {
        try {
          const { data } = await api.get(`/parking?property_id=${propId}`);
          const rows = (data?.items || []).filter((p) => p.unit === user?.unit);
          setSpots(rows);
        } catch (err) { if (typeof console !== 'undefined') console.debug('pages/resident/ResidentParkingPage.jsx: swallowed', err); }
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [user?.id]);

  // R72 — live sync: refresh when admin approves/denies vehicle or a
  // guest pass is scanned at the gate.
  useEffect(() => {
    if (!user?.id) return;
    const unsub = subscribe(`user:${user.id}`, ({ event }) => {
      if (event === "vehicle.status_changed" || event === "guest_pass.scanned") {
        load();
      }
    });
    return unsub;
  }, [subscribe, user?.id]);

  const openNew = () => {
    setEditing(null);
    setForm({ make: "", model: "", year: "", color: "", plate: "", permit_number: "", notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({
      make: v.make || "", model: v.model || "", year: v.year || "",
      color: v.color || "", plate: v.plate || "",
      permit_number: v.permit_number || "", notes: v.notes || "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.make.trim() || !form.model.trim() || !form.plate.trim()) {
      toast.error("Make, model, and plate are required");
      return;
    }
    const payload = { ...form };
    payload.year = payload.year ? Number(payload.year) : undefined;
    // Prune blanks so the backend doesn't overwrite fields with empty strings
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "" || payload[k] === undefined) delete payload[k];
    });
    try {
      if (editing) {
        await api.patch(`/me/vehicles/${editing.id}`, payload);
        toast.success("Vehicle updated — pending re-approval");
      } else {
        await api.post("/me/vehicles", payload);
        toast.success("Vehicle registered — pending admin approval");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (vid) => {
    try {
      await api.delete(`/me/vehicles/${vid}`);
      toast.success("Vehicle removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const statusPill = (s) => ({
    approved: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    denied: "bg-red-500/10 border-red-500/30 text-red-300",
    pending: "bg-amber-500/10 border-amber-500/30 text-amber-300",
  }[s] || "bg-zinc-500/10 border-zinc-500/30 text-zinc-300");

  return (
    <div data-testid="resident-parking-page">
      <PageHeader
        eyebrow="Facilities"
        title="Parking & Mailboxes"
        subtitle="Register your vehicles, view your parking spot and mailbox assignment."
      />

      {/* MAILBOX */}
      <Card className="p-5 border-white/10 bg-card mb-4" data-testid="my-mailbox-card">
        <div className="flex items-center gap-2 overline mb-3">
          <Mailbox className="h-4 w-4 text-orange-400" /> My Mailbox
        </div>
        {mailbox?.assigned ? (
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <div className="text-xs text-zinc-500">Box #</div>
              <div className="text-2xl font-semibold font-display text-orange-300"
                   data-testid="mailbox-box-number">{mailbox.mailbox.box_number}</div>
            </div>
            {mailbox.mailbox.unit && (
              <div>
                <div className="text-xs text-zinc-500">Unit</div>
                <div className="text-lg">{mailbox.mailbox.unit}</div>
              </div>
            )}
            {mailbox.mailbox.location && (
              <div>
                <div className="text-xs text-zinc-500">Location</div>
                <div className="text-sm">{mailbox.mailbox.location}</div>
              </div>
            )}
            {mailbox.mailbox.key_count && (
              <div>
                <div className="text-xs text-zinc-500 flex items-center gap-1">
                  <KeyRound className="h-3 w-3" /> Keys issued
                </div>
                <div className="text-sm">{mailbox.mailbox.key_count}</div>
              </div>
            )}
            {mailbox.mailbox.notes && (
              <div className="w-full text-xs text-zinc-400 mt-2">{mailbox.mailbox.notes}</div>
            )}
          </div>
        ) : (
          <div className="text-sm text-zinc-400 flex items-center gap-2"
               data-testid="mailbox-unassigned">
            <Info className="h-4 w-4 text-amber-400" />
            {mailbox?.hint || "No mailbox assigned yet. Contact your property manager."}
          </div>
        )}
      </Card>

      {/* PARKING SPOTS (read-only) */}
      {spots.length > 0 && (
        <Card className="p-5 border-white/10 bg-card mb-4" data-testid="my-spots-card">
          <div className="overline mb-3">Assigned Parking Spot{spots.length === 1 ? "" : "s"}</div>
          <div className="flex flex-wrap gap-3">
            {spots.map((s) => (
              <div key={s.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 min-w-[180px]"
                   data-testid={`spot-${s.id}`}>
                <div className="text-xs text-zinc-500">Spot</div>
                <div className="text-xl font-semibold font-display text-orange-300">{s.spot}</div>
                {s.vehicle && <div className="text-xs text-zinc-400 mt-1">{s.vehicle}</div>}
                {s.plate && <div className="text-xs text-zinc-500 font-mono">{s.plate}</div>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* VEHICLES */}
      <Card className="p-5 border-white/10 bg-card" data-testid="my-vehicles-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 overline">
            <Car className="h-4 w-4 text-orange-400" />
            My Vehicles ({vehicles.length})
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openNew}
                className="rounded-full bg-orange-500 hover:bg-orange-600 text-black"
                data-testid="register-vehicle-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Register vehicle
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="vehicle-dialog">
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Edit vehicle" : "Register a vehicle"}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Make *</Label>
                  <Input data-testid="vehicle-make" value={form.make}
                         onChange={(e) => setForm({ ...form, make: e.target.value })} placeholder="Toyota" />
                </div>
                <div>
                  <Label>Model *</Label>
                  <Input data-testid="vehicle-model" value={form.model}
                         onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Camry" />
                </div>
                <div>
                  <Label>Year</Label>
                  <Input data-testid="vehicle-year" type="number" value={form.year}
                         onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="2024" />
                </div>
                <div>
                  <Label>Color</Label>
                  <Input data-testid="vehicle-color" value={form.color}
                         onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="Blue" />
                </div>
                <div>
                  <Label>License Plate *</Label>
                  <Input data-testid="vehicle-plate" value={form.plate}
                         onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })}
                         placeholder="ABC-1234" className="font-mono uppercase" />
                </div>
                <div>
                  <Label>Permit # (if issued)</Label>
                  <Input data-testid="vehicle-permit" value={form.permit_number}
                         onChange={(e) => setForm({ ...form, permit_number: e.target.value })} placeholder="P-2026-014" />
                </div>
                <div className="md:col-span-2">
                  <Label>Notes</Label>
                  <Input data-testid="vehicle-notes" value={form.notes}
                         onChange={(e) => setForm({ ...form, notes: e.target.value })}
                         placeholder="Guest vehicle, weekends only, etc." />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}
                        data-testid="vehicle-cancel">Cancel</Button>
                <Button onClick={submit} data-testid="vehicle-save"
                        className="rounded-full bg-orange-500 hover:bg-orange-600 text-black">
                  {editing ? "Save changes" : "Register"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading && vehicles.length === 0 ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : vehicles.length === 0 ? (
          <div className="text-sm text-zinc-400" data-testid="vehicles-empty">
            No vehicles registered yet. Click <strong>Register vehicle</strong> to add your first.
          </div>
        ) : (
          <div className="space-y-2">
            {vehicles.map((v) => (
              <div key={v.id}
                   className="rounded-lg border border-white/10 bg-white/[0.02] p-3 flex flex-wrap items-center gap-3 justify-between"
                   data-testid={`vehicle-${v.id}`}>
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">
                      {v.year ? `${v.year} ` : ""}{v.make} {v.model}
                      {v.color && <span className="text-zinc-500"> · {v.color}</span>}
                    </div>
                    <Badge variant="outline" className={statusPill(v.status)}>
                      {v.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-zinc-400 mt-1 font-mono">{v.plate}</div>
                  {v.permit_number && (
                    <div className="text-xs text-zinc-500 mt-0.5">
                      Permit #{v.permit_number}
                    </div>
                  )}
                  {v.spot_number && (
                    <div className="text-xs text-emerald-400 mt-0.5">
                      Assigned spot: {v.spot_number}
                    </div>
                  )}
                  {v.decision_reason && v.status === "denied" && (
                    <div className="text-xs text-red-300 mt-1">
                      Reason: {v.decision_reason}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="border-white/10"
                          onClick={() => openEdit(v)}
                          data-testid={`vehicle-edit-${v.id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline"
                              className="border-red-500/30 text-red-300 hover:bg-red-500/10"
                              data-testid={`vehicle-delete-${v.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid={`vehicle-delete-confirm-${v.id}`}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove {v.make} {v.model}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will delete your registration for plate <span className="font-mono">{v.plate}</span>.
                          You&apos;ll need to re-register the vehicle if you drive it back.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`vehicle-delete-cancel-${v.id}`}>Keep it</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(v.id)}
                                            data-testid={`vehicle-delete-yes-${v.id}`}
                                            className="bg-red-500 hover:bg-red-600 text-white">
                          Remove vehicle
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="mt-4">
        <GuestPassesCard />
      </div>
    </div>
  );
}
